import { useState, useRef, useEffect, useCallback } from "react";
import { MapPin, ChevronDown, Search, Loader2 } from "lucide-react";
import CITIES from "@/lib/cities";

export default function CityCombobox({ value, onSelect, className = "" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || "");
  const [remote, setRemote] = useState([]);
  const [searching, setSearching] = useState(false);
  const ref = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // local city filter
  const local = CITIES.filter((c) =>
    c.name.toLowerCase().includes(query.toLowerCase()) ||
    c.province.toLowerCase().includes(query.toLowerCase())
  ).slice(0, 4);

  // Nominatim geocoding — search when ≥3 chars
  const fetchRemote = useCallback((q) => {
    abortRef.current?.abort();
    if (q.length < 3) { setRemote([]); setSearching(false); return; }

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setSearching(true);

    fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q + ", Indonesia")}&format=json&limit=6&addressdetails=1`, {
      signal: ctrl.signal,
      headers: { "Accept-Language": "id" },
    })
      .then((r) => r.json())
      .then((results) => {
        setRemote(results.map((r) => ({
          name: r.address?.city || r.address?.town || r.address?.village || r.display_name.split(",")[0],
          province: r.display_name.split(", ").slice(1, 3).join(", "),
          lat: parseFloat(r.lat),
          lng: parseFloat(r.lon),
          _remote: true,
        })));
      })
      .catch(() => {}) // abort or network error
      .finally(() => setSearching(false));

    return () => ctrl.abort();
  }, []);

  // debounce remote search
  useEffect(() => {
    const t = setTimeout(() => fetchRemote(query), 300);
    return () => clearTimeout(t);
  }, [query, fetchRemote]);

  // deduplicate remote results that match local cities
  const seen = new Set(local.map((c) => c.name));
  const uniqueRemote = remote.filter((c) => !seen.has(c.name));
  const results = [...local, ...uniqueRemote].slice(0, 8);

  const pick = (c) => {
    setQuery(c.name);
    onSelect(c);
    setOpen(false);
    setRemote([]);
  };

  return (
    <div className={`relative ${className}`} ref={ref} data-testid="city-combobox">
      <MapPin className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5 pointer-events-none" />
      <input
        data-testid="city-search-input"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); onSelect({ name: e.target.value }); }}
        onFocus={() => setOpen(true)}
        placeholder="Cari kota atau lokasi..."
        className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-9 py-2.5 text-sm focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none transition"
      />
      {searching ? (
        <Loader2 className="w-4 h-4 text-sky-400 absolute right-3 top-3.5 animate-spin pointer-events-none" />
      ) : (
        <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-3.5 pointer-events-none" />
      )}
      {open && results.length > 0 && (
        <div className="absolute z-30 mt-1.5 w-full bg-white rounded-xl border border-slate-100 shadow-lg max-h-64 overflow-y-auto">
          {local.length > 0 && query.length < 3 && (
            <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Kota besar</div>
          )}
          {local.map((c) => (
            <button
              key={`local-${c.name}`}
              type="button"
              onClick={() => pick(c)}
              data-testid={`city-option-${c.name.toLowerCase().replace(/\s/g, "-")}`}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-sky-50 flex items-center justify-between transition"
            >
              <span className="font-medium text-slate-700">{c.name}</span>
              <span className="text-xs text-slate-400">{c.province}</span>
            </button>
          ))}
          {uniqueRemote.length > 0 && (
            <>
              <div className="px-3 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider border-t border-slate-100">Hasil pencarian</div>
              {uniqueRemote.map((c, i) => (
                <button
                  key={`remote-${i}-${c.name}`}
                  type="button"
                  onClick={() => pick(c)}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-sky-50 flex items-center justify-between transition"
                >
                  <span className="font-medium text-slate-700 flex items-center gap-1.5">
                    <MapPin className="w-3 h-3 text-sky-400 shrink-0" />
                    {c.name}
                  </span>
                  <span className="text-xs text-slate-400 truncate max-w-[40%] text-right">{c.province}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
